import { CalculatorFormValues, StepName } from 'common/types';
import { MainLayout } from 'layouts';
import { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { Result, WizardForm } from './components';
import { gsap } from 'gsap';
import { Co2FootprintVO } from 'vo';
import { useSteps, useFormValues, useStepCalculationHandlers, useFetchAnswer } from './hooks';
import { selectors, useAppSelector } from 'store';
import * as utils from 'utils';
import { Loader } from '@net0-calc/common/components/ui';
import { DEVICE } from '@net0-calc/common/assets/styles';
import { StepBar } from '@net0-calc/common/components';
import { SidebarStepNav } from 'components';
import { api } from '@monaco-travel/api';

export const MainPage = () => {
  const isDictionaryLoading = useAppSelector(selectors.dictionary.selectIsDictionaryLoading);
  const [isResultPageSelected, setIsResultPageSelected] = useState(false);

  const { formValues, setFormValues } = useFormValues();
  const { co2FootprintsVOs, distanceVO, handleStepCalculations } = useStepCalculationHandlers({ formValues });
  const [steps, { changeCurrentStepIndex, currentStepIndex, currentStepName, isLastStep }] = useSteps({
    formValues,
    distanceVO,
  });
  const co2FootprintSumVO = Co2FootprintVO.sum(Object.values(co2FootprintsVOs));

  const sharedAnswerId = utils.getFromSearchString('answerId');
  const [answer, { isLoading: isAnswerLoading }] = useFetchAnswer(sharedAnswerId);

  const [answerId, setAnswerId] = useState<string>();

  const sendAnswers = () => {
    const urlParams = new URLSearchParams(window.location.search);
    api
      .sendAnswers({
        answers: {
          locationOrigin: window.location.origin,
          formValues,
          co2Footprint: {
            sum: {
              totalKg: co2FootprintSumVO.total.kg,
              perPersonKg: co2FootprintSumVO.perPerson.kg,
            },
            fromTo: {
              totalKg: co2FootprintsVOs.fromTo.total.kg,
              perPersonKg: co2FootprintsVOs.fromTo.perPerson.kg,
            },
            fromAirportToCity: {
              totalKg: co2FootprintsVOs.fromAirportToCity.total.kg,
              perPersonKg: co2FootprintsVOs.fromAirportToCity.perPerson.kg,
            },
            accomodation: {
              totalKg: co2FootprintsVOs.accomodation.total.kg,
              perPersonKg: co2FootprintsVOs.accomodation.perPerson.kg,
            },
            aroundCity: {
              totalKg: co2FootprintsVOs.aroundCity.total.kg,
              perPersonKg: co2FootprintsVOs.aroundCity.perPerson.kg,
            },
          },
          metadata: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            languages: navigator.languages ? Array.from(navigator.languages) : [navigator.language],
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            devicePixelRatio: window.devicePixelRatio,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            referrer: document.referrer || null,
            utmSource: urlParams.get('utm_source') || null,
            utmMedium: urlParams.get('utm_medium') || null,
            utmCampaign: urlParams.get('utm_campaign') || null,
            utmTerm: urlParams.get('utm_term') || null,
            utmContent: urlParams.get('utm_content') || null,
            platform: navigator.platform || null,
            cookieEnabled: navigator.cookieEnabled,
            online: navigator.onLine,
            touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
            submittedAt: new Date().toISOString(),
          },
        },
      })
      .then(({ data }) => {
        setAnswerId(data);
      })
      .catch(console.log);
  };

  useEffect(() => {
    if (answer) {
      setFormValues(answer.answers.formValues);
    }
  }, [answer]);

  const isLoading = isDictionaryLoading || isAnswerLoading;
  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader size={80} color="var(--color2)" />
      </LoaderWrapper>
    );
  }

  const handleStepSubmit = async () => {
    handleStepCalculations(currentStepName);

    if (isLastStep) {
      setIsResultPageSelected(true);
      sendAnswers();
      return;
    }

    // Animate icon while changing step immediately (parallel)
    const stepIconEl = document.querySelector('#step-icon-' + currentStepIndex);
    if (stepIconEl) {
      gsap.fromTo(
        stepIconEl,
        { scale: 1.4, opacity: 0.6 },
        { scale: 1, opacity: 1, duration: 0.3, ease: 'power2.out' },
      );
    }
    changeCurrentStepIndex(current => current + 1);
  };

  const handleBackButtonClick = () => {
    if (isResultPageSelected) {
      setIsResultPageSelected(false);
      changeCurrentStepIndex(steps.length - 1);
    } else {
      changeCurrentStepIndex(currentStepIndex - 1);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sidebarContent = !isResultPageSelected ? (
    <SidebarStepNav<CalculatorFormValues, StepName>
      steps={steps}
      currentStepIndex={currentStepIndex}
      onStepClick={changeCurrentStepIndex}
    />
  ) : undefined;

  return (
    <MainLayout
      isBackButtonVisible={currentStepIndex > 0 || isResultPageSelected}
      isResultPage={isResultPageSelected}
      onPrevStep={handleBackButtonClick}
      sidebarContent={sidebarContent}>
      {isResultPageSelected && (
        <Result
          answerId={answerId}
          formValues={formValues}
          co2FootprintSumVO={co2FootprintSumVO}
          co2FootprintsVOs={co2FootprintsVOs}
        />
      )}
      {!isResultPageSelected && (
        <PageWrap>
          <MobileStepBarWrapper>
            <StepBar<CalculatorFormValues, StepName>
              steps={steps}
              currentStepIndex={currentStepIndex}
              onStepClick={changeCurrentStepIndex}
            />
          </MobileStepBarWrapper>
          <MiddleContainer>
            <WizardForm<CalculatorFormValues>
              initialValues={formValues}
              currentPageIndex={currentStepIndex}
              onStepSubmit={handleStepSubmit}
              co2FootprintSumVO={co2FootprintSumVO}
              onChangeFormSpy={state => setFormValues(state.values)}
              pages={steps.map(({ page }) => page)}
            />
          </MiddleContainer>
        </PageWrap>
      )}
    </MainLayout>
  );
};

const PageWrap = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MobileStepBarWrapper = styled.div`
  min-height: 40px;
  margin-bottom: 12px;
  z-index: 200;

  @media ${DEVICE.laptop} {
    display: none;
  }
`;

const MiddleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const LoaderWrapper = styled.div`
  display: flex;
  min-height: 100%;
  align-items: center;
  justify-content: center;
`;

// const RightContainer = styled.div`
//   display: none;
//   background-color: var(--color5);
//   z-index: 200;

//   @media ${DEVICE.laptop} {
//     display: block;
//     width: 100%;
//   }

//   @media ${DEVICE.desktop} {
//     margin-left: 133px;
//   }
// `;
